import React, { useState } from "react";
import { Link } from "react-router";
import { Plus, Search } from "lucide-react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input } from "../../components/ui/Input";
import { useQuietPeriods } from "../../hooks/useQuietPeriods";
import { QuietPeriodFilters } from "../../types/quiet_period";

const QuietPeriodsPage: React.FC = () => {
  const [filters, setFilters] = useState<QuietPeriodFilters>({
    page: 1,
    page_size: 10,
  });

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuietPeriods(filters);
  const quietPeriods = response?.quiet_periods || [];
  const totalCount = response?.total || 0;
  const totalPages =
    response?.total_pages || Math.ceil(totalCount / (filters.page_size || 10));

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  };
  const handlePageChange = (page: number) =>
    setFilters((prev) => ({ ...prev, page }));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message="Failed to load dynamic dates. Please try again."
        onRetry={() => refetch()}
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dynamic Dates
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage maintenance windows and holiday blackout periods
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/quiet-periods/new"
            className="btn-primary inline-flex items-center px-4 py-2 font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Dynamic Date
          </Link>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search dynamic dates..."
              variant="shadow-sm"
              showLabel={false}
              className="pl-10"
              value={filters.search || ""}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {quietPeriods.map((qp) => (
            <li key={qp.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
              <Link to={`/quiet-periods/${qp.id}`} className="block">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-indigo-600 truncate">
                    {qp.name}
                  </p>
                  <div className="ml-2 flex-shrink-0 flex">
                    <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {new Date(qp.from).toLocaleString()} →{" "}
                      {new Date(qp.to).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex sm:items-center sm:space-x-2">
                    <p className="flex items-center text-sm text-gray-500 truncate max-w-2xl">
                      {qp.description || "—"}
                    </p>
                    {qp.tags && qp.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {qp.tags.map((t, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
          {quietPeriods.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500">
              No dynamic dates found
            </li>
          )}
        </ul>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">Total: {totalCount}</div>
          <div className="space-x-2">
            <button
              className="btn-secondary px-3 py-1 rounded-md"
              disabled={(filters.page || 1) <= 1}
              onClick={() => handlePageChange((filters.page || 1) - 1)}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {filters.page || 1} of {totalPages}
            </span>
            <button
              className="btn-secondary px-3 py-1 rounded-md"
              disabled={(filters.page || 1) >= totalPages}
              onClick={() => handlePageChange((filters.page || 1) + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuietPeriodsPage;
